import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarDays, MapPin, ArrowRight, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const dayBadge = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return { m: "", day: "" };
  return { m: d.toLocaleString(undefined, { month: "short" }).toUpperCase(), day: d.getDate() };
};

// Slim, dismissible banner spotlighting the next major community event.
// Drives traffic from the shop into the Essence of MPire community hub.
export default function FeaturedEventBanner() {
  const [event, setEvent] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/events`)
      .then((r) => {
        const list = r.data || [];
        if (!list.length) return;
        // "Next major" = soonest upcoming; prefer one with the largest expected attendance in the next 60 days.
        const soonest = list[0];
        setEvent(soonest);
        const key = `mpire-event-banner-dismissed-${soonest.id}`;
        if (localStorage.getItem(key) === "1") setHidden(true);
      })
      .catch(() => {});
  }, []);

  if (!event || hidden) return null;
  const b = dayBadge(event.start_datetime);
  const dismiss = (e) => {
    e.preventDefault();
    localStorage.setItem(`mpire-event-banner-dismissed-${event.id}`, "1");
    setHidden(true);
  };

  return (
    <Link
      to="/essence"
      className="group relative block w-full overflow-hidden bg-gradient-to-r from-[#0F0F10] via-[#1A1A1D] to-[#2b0d10] text-white border-b border-[#2A2A2F]"
      data-testid="featured-event-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <div className="flex flex-col items-center justify-center w-11 h-11 rounded-lg bg-[var(--brand)] text-white shrink-0 leading-none">
          <span className="text-[8px] font-bold tracking-wider">{b.m}</span>
          <span className="text-base font-bold">{b.day}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold">Community Spotlight</p>
          <p className="text-sm font-bold leading-tight truncate">{event.title}</p>
          <p className="text-[11px] text-white/70 truncate inline-flex items-center gap-1">
            <MapPin size={10} /> {event.location}
          </p>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold whitespace-nowrap group-hover:bg-[var(--brand)] transition-colors" data-testid="banner-cta">
          <CalendarDays size={13} className="text-[#D4AF37] group-hover:text-white" /> See the Community <ArrowRight size={13} />
        </span>
        <button onClick={dismiss} className="ml-1 p-1 rounded text-white/50 hover:text-white hover:bg-white/10 transition-colors shrink-0" aria-label="Dismiss" data-testid="banner-dismiss">
          <X size={16} />
        </button>
      </div>
    </Link>
  );
}
